import { DefaultEventParams, EventParams } from '../Analytics/Event.types';
import { getOfflineIdFromStorage } from '../Analytics/utils/userIdSettings';

declare const window: Window & { dataLayer: Record<string, any>[] };

export class GTag {
  public isLoaded: boolean;
  private _gtag?: (...args: unknown[]) => void;
  private _measurementId?: string;
  public offlineId?: string;

  constructor(
    trackingId = process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID
  ) {
    this.isLoaded = false;
    this._gtag = undefined;
    this._measurementId = trackingId;
    this.offlineId = undefined;

    this.initialize();
  }

  public initialize(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (!this._measurementId) {
      return console.error(
        'You need to set a measurementId to REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID environment variables of your project.'
      );
    }

    this.initializeInstanceForWeb();
  }

  private initializeInstanceForWeb(): void {
    const gtagUrl = 'https://www.googletagmanager.com/gtag/js';
    const dataLayerQuery = 'l=dataLayer&cx=c';

    const isExistingInstance =
      document.querySelectorAll(
        `script[src="${gtagUrl}?id=${this._measurementId}"]`
      ).length ||
      document.querySelectorAll(
        `script[src="${gtagUrl}?id=${this._measurementId}&${dataLayerQuery}"]`
      ).length;

    if (!this.isLoaded && !isExistingInstance) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `${gtagUrl}?id=${this._measurementId}`;

      // NOUNCE WILL BE IMPLEMENTED IN V2
      // script.setAttribute("nonce", nonce);

      document.body.appendChild(script);
      this.isLoaded = true;
    }

    window.dataLayer = window.dataLayer || [];

    this._gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    const offlineId = getOfflineIdFromStorage();
    this.offlineId = offlineId;

    this.setGtagConfig({ offlineId });

    if (this.isLoaded) {
      this._gtag('js', new Date());
    }
  }

  public setUserId(user_id: string): void {
    if (!this._gtag || !this._measurementId) return;
    return this._gtag('config', this._measurementId, {
      user_id,
    });
  }

  public setGtagConfig(options?: DefaultEventParams) {
    if (!this._gtag || !this._measurementId || !options) return;
    return this._gtag('config', this._measurementId, options);
  }

  public logEvent(eventName: string, eventParams?: EventParams): void {
    if (!this._gtag) return;

    const debug_mode = eventParams?.options?.debug_mode;

    const params = {
      send_to: this._measurementId,
      offlineId: this.offlineId,
      ...(debug_mode ? { debug_mode } : {}),
      ...(eventParams || {}),
    };

    return this._gtag('event', eventName, params);
  }
}
